import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { FiExternalLink, FiCopy, FiCheck, FiX } from "react-icons/fi";

// Google Chrome logo asset from /public
const CHROME_LOGO = `${process.env.PUBLIC_URL || ""}/chrome.png`;

/**
 * OpenInBrowserGate
 * -----------------
 * When the client URL carries `?met=true` and the page is opened inside an
 * in-app browser / WebView (Instagram, Facebook, etc. — i.e. NOT a real Chrome
 * style browser), we show a centered glass box over a blurred background asking
 * the user to open the link in their browser. Many flows (Google login, file
 * downloads, payments) break inside in-app WebViews — this nudges users out.
 *
 * Trigger:
 *   ?met=true   -> show the box ONLY when an in-app WebView is detected.
 *   ?met=force  -> always show (handy for previewing/testing on desktop Chrome).
 */

// Detect common in-app browsers / Android System WebViews.
const detectInAppBrowser = () => {
    if (typeof navigator === "undefined") return false;
    const ua = (navigator.userAgent || navigator.vendor || "").toLowerCase();

    // Real standalone browsers on iOS we explicitly trust.
    if (ua.includes("crios")) return false; // Chrome on iOS
    if (ua.includes("fxios")) return false; // Firefox on iOS
    if (ua.includes("edgios")) return false; // Edge on iOS

    // Known in-app WebView signatures.
    const inAppMatchers = [
        "fban", "fbav", "fb_iab", "fbios", // Facebook / Messenger
        "instagram",
        "line/",
        "micromessenger", // WeChat
        "twitter",
        "whatsapp",
        "snapchat",
        "pinterest",
        "messenger",
        "tiktok", "musical_ly",
        "linkedinapp",
        "gsa/", // Google app
    ];
    if (inAppMatchers.some((m) => ua.includes(m))) return true;

    // Android System WebView marker.
    if (ua.includes("; wv)") || ua.includes("; wv;")) return true;

    return false;
};

const getPlatform = () => {
    const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "").toLowerCase();
    return {
        isAndroid: /android/.test(ua),
        isIOS: /iphone|ipad|ipod/.test(ua),
    };
};

const OpenInBrowserGate = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    // Capture the ?met value ONCE on first mount, before we strip it from the URL.
    const [met] = useState(() => searchParams.get("met"));

    const [dismissed, setDismissed] = useState(false);
    const [copied, setCopied] = useState(false);

    const { isAndroid, isIOS } = useMemo(getPlatform, []);

    // Strip ?met from the address bar on load so it's never revealed in the URL.
    // The gate still works because its value was captured above.
    useEffect(() => {
        if (!met) return;
        if (searchParams.has("met")) {
            const next = new URLSearchParams(searchParams);
            next.delete("met");
            setSearchParams(next, { replace: true });
        }
    }, [met, searchParams, setSearchParams]);

    // Decide whether the gate should be shown for this URL + environment.
    const shouldShow = useMemo(() => {
        if (met === "force") return true;          // preview / testing
        if (met === "true") return detectInAppBrowser();
        return false;
    }, [met]);

    const open = shouldShow && !dismissed;

    // Lock background scroll while the box is open.
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    if (!open) return null;

    // The URL we copy / open must NOT carry the gate's own ?met param.
    const cleanUrlObj = (() => {
        try {
            const u = new URL(window.location.href);
            u.searchParams.delete("met");
            return u;
        } catch (e) {
            return null;
        }
    })();
    const cleanUrl = cleanUrlObj ? cleanUrlObj.toString() : window.location.href;

    const handleOpenInBrowser = () => {
        const hostPathSearch = cleanUrlObj
            ? `${cleanUrlObj.host}${cleanUrlObj.pathname}${cleanUrlObj.search}`
            : `${window.location.host}${window.location.pathname}`;

        if (isAndroid) {
            // Hand the URL to Chrome via an Android intent (with a web fallback).
            const fallback = encodeURIComponent(cleanUrl);
            window.location.href =
                `intent://${hostPathSearch}` +
                `#Intent;scheme=https;package=com.android.chrome;` +
                `S.browser_fallback_url=${fallback};end`;
            return;
        }

        if (isIOS) {
            // Try the Chrome-iOS URL scheme; if Chrome isn't installed nothing
            // happens and the user can still use "Copy link" + the steps below.
            window.location.href = `googlechromes://${hostPathSearch}`;
            return;
        }

        // Desktop / unknown — just open in a new tab.
        window.open(cleanUrl, "_blank", "noopener");
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(cleanUrl);
        } catch (e) {
            // Fallback for older WebViews without the async clipboard API.
            const ta = document.createElement("textarea");
            ta.value = cleanUrl;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); } catch (_) {}
            document.body.removeChild(ta);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const steps = isIOS
        ? ['Tap the “•••” / share icon', 'Choose “Open in Safari / Browser”']
        : ['Tap the “⋮” menu (top-right)', 'Choose “Open in browser / Chrome”'];

    const content = (
        <div
            className="fixed inset-0 z-[2000] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
        >
            {/* Blurred backdrop */}
            <div
                aria-hidden
                className="absolute inset-0"
                style={{
                    background: "rgba(6,4,10,0.6)",
                    backdropFilter: "blur(14px) saturate(140%)",
                    WebkitBackdropFilter: "blur(14px) saturate(140%)",
                }}
            />

            {/* Centered box */}
            <div
                className="relative w-full max-w-sm rounded-3xl p-6 text-center"
                style={{
                    background:
                        "linear-gradient(165deg, rgba(28,18,48,0.96), rgba(10,8,16,0.98))",
                    border: "1px solid rgba(255,255,255,0.14)",
                    boxShadow:
                        "0 30px 70px -18px rgba(127,0,255,0.5), 0 14px 30px -12px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.16)",
                }}
            >
                {/* Close */}
                <button
                    onClick={() => setDismissed(true)}
                    aria-label="Close"
                    className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white"
                    style={{
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.12)",
                    }}
                >
                    <FiX className="text-sm" />
                </button>

                {/* Icon — official-style Google Chrome logo on a white tile */}
                <div
                    className="mx-auto mb-4 w-16 h-16 rounded-2xl flex items-center justify-center"
                    style={{
                        background: "#ffffff",
                        boxShadow:
                            "0 12px 28px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.6)",
                    }}
                >
                    <img
                        src={CHROME_LOGO}
                        alt="Google Chrome"
                        className="w-10 h-10 object-contain"
                    />
                </div>

                <h3 className="text-client-font font-bold text-lg tracking-tight mb-1.5">
                    Open in your browser
                </h3>
                <p className="text-client-font/65 text-sm leading-relaxed mb-5">
                    For the best experience (login, downloads &amp; more), please open
                    this page in your browser instead of the in-app view.
                </p>

                {/* Primary action */}
                <button
                    onClick={handleOpenInBrowser}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-white font-semibold text-sm mb-2.5"
                    style={{
                        background: "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                        boxShadow:
                            "0 10px 24px -8px rgba(127,0,255,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
                    }}
                >
                    <FiExternalLink className="text-base" />
                    Open in Browser
                </button>

                {/* Copy link */}
                <button
                    onClick={handleCopy}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-client-font/85 font-medium text-sm"
                    style={{
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.12)",
                    }}
                >
                    {copied ? (
                        <>
                            <FiCheck className="text-base" style={{ color: "#34d399" }} />
                            Link copied!
                        </>
                    ) : (
                        <>
                            <FiCopy className="text-base" />
                            Copy link
                        </>
                    )}
                </button>

                {/* Manual steps */}
                <div
                    className="mt-5 pt-4 text-left"
                    style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
                >
                    <p className="text-client-font/45 text-[11px] uppercase tracking-[0.18em] font-semibold mb-2">
                        Or do it manually
                    </p>
                    <ol className="space-y-1.5">
                        {steps.map((s, i) => (
                            <li
                                key={i}
                                className="flex items-center gap-2.5 text-client-font/75 text-[13px]"
                            >
                                <span
                                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold"
                                    style={{
                                        background: "rgba(127,0,255,0.25)",
                                        border: "1px solid rgba(127,0,255,0.4)",
                                        color: "#FCFA02",
                                    }}
                                >
                                    {i + 1}
                                </span>
                                {s}
                            </li>
                        ))}
                    </ol>
                </div>
            </div>
        </div>
    );

    return createPortal(content, document.body);
};

export default OpenInBrowserGate;
